import React, { useEffect, createElement, Component } from 'react';
import { motion } from 'framer-motion';
import { Download, Anchor, Activity, MessageCircle, ShieldCheck, ArrowRight } from 'lucide-react';
// Meta Pixel type declaration
declare global {
  interface Window {
    fbq: (...args: any[]) => void;
    _fbq: (...args: any[]) => void;
  }
}
const initMetaPixel = () => {
  if (window.fbq) return;
  const n: any = window.fbq = function () {
    n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
  };
  if (!window._fbq) window._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = '2.0';
  n.queue = [];
  const t = document.createElement('script');
  t.async = true;
  t.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(t);
  window.fbq('init', '26193603199265');
  window.fbq('track', 'PageView');
};
const trackAddToCart = () => {
  if (window.fbq) {
    window.fbq('track', 'AddToCart');
  }
};
// --- Utility Components ---
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.8,
  delay,
  ease: [0.21, 0.47, 0.32, 0.98]
}} className={className} data-id="element-1">
    {children}
  </motion.div>;
const CTAButton = ({
  children,
  href = '#offer',
  className = '',
  onClick
}: {
  children: React.ReactNode;
  href?: string;
  className?: string;
  onClick?: () => void;
}) => <a href="https://guiasparavivir.com/cart/52588544557345:1" target="_blank" rel="noopener noreferrer" onClick={onClick} className={`inline-flex items-center justify-center gap-2 bg-[#C4704B] hover:bg-[#A85A38] text-white px-8 py-4 rounded-full font-sans font-medium text-lg transition-all duration-300 transform hover:scale-[1.02] hover:shadow-lg cursor-pointer ${className}`} data-id="element-2">
    {children}
  </a>;
// --- Sections ---
const UrgencyBar = () => <div className="bg-[#D4DDD1] text-[#2D2D2D] py-2.5 px-4 text-center text-sm font-medium flex items-center justify-center gap-2 z-50 relative" data-id="element-3">
    <Download className="w-4 h-4" data-id="element-4" />
    <span data-id="element-5">
      Protocolo de emergencia para crisis sensoriales (Edición 2024). Disponible
      para descarga inmediata.
    </span>
  </div>;
const HeroSection = () => <section className="relative min-h-[90vh] flex flex-col items-center justify-center px-4 py-20 bg-[#FAF8F5] overflow-hidden" data-id="element-6">
    {/* Subtle background decoration */}
    <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-7">
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-[#F5F0EB] blur-3xl opacity-70" data-id="element-8"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-[#D4DDD1] blur-3xl opacity-30" data-id="element-9"></div>
    </div>

    <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-10">
      <FadeIn data-id="element-11">
        <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#2D2D2D] leading-[1.15] tracking-tight mb-6" data-id="element-12">
          Hacer que la paz regrese a casa esta noche es posible.{' '}
          <span className="text-[#C4704B] italic" data-id="element-13">
            Incluso si sientes que ya lo has intentado todo.
          </span>
        </h1>
      </FadeIn>

      <FadeIn delay={0.2} data-id="element-14">
        <p className="font-sans text-lg md:text-xl text-[#2D2D2D]/80 max-w-2xl mx-auto leading-relaxed mb-10" data-id="element-15">
          Descubre el método de 3 pasos para calmar crisis sensoriales y
          meltdowns en minutos, basado en neurociencia aplicada y diseñado para
          padres que ya no tienen fuerzas para más teoría.
        </p>
      </FadeIn>

      <FadeIn delay={0.4} data-id="element-16">
        <CTAButton href="#offer" onClick={trackAddToCart} data-id="element-17">
          QUIERO RECUPERAR LA CALMA <ArrowRight className="w-5 h-5 ml-1" data-id="element-18" />
        </CTAButton>
      </FadeIn>
    </div>
  </section>;
const ValidationSection = () => <section className="py-24 px-4 bg-[#F5F0EB]" data-id="element-19">
    <div className="max-w-3xl mx-auto" data-id="element-20">
      <FadeIn data-id="element-21">
        <div className="prose prose-lg md:prose-xl text-[#2D2D2D] font-serif leading-relaxed text-center md:text-left" data-id="element-22">
          <p className="text-2xl md:text-3xl mb-8 font-medium italic text-[#C4704B]" data-id="element-23">
            "Sabemos cómo se siente el silencio después de una crisis."
          </p>

          <p className="mb-6" data-id="element-24">
            Ese silencio pesado en la cocina, cuando tu hijo por fin se ha
            dormido o se ha calmado, pero tú sigues temblando por dentro. Te
            preguntas qué hiciste mal, por qué hoy fue tan difícil y cuánto
            tiempo más podrás aguantar este ritmo.
          </p>

          <p className="mb-6 font-medium" data-id="element-25">
            No es que tu hijo 'se porte mal'. No es que tú seas 'un mal padre'.
            Es que su sistema nervioso está saturado, y el tuyo también.
          </p>

          <p className="text-[#2D2D2D]/80 font-sans text-base md:text-lg mt-10 p-6 bg-white/50 rounded-2xl border border-[#D4DDD1]/50" data-id="element-26">
            <strong data-id="element-27">El Método: Cero Crisis</strong> no es un libro de 300
            páginas sobre teoría del autismo. Es un manual de intervención
            rápida para el momento exacto en que el mundo se vuelve 'demasiado'
            para tu hijo.
          </p>
        </div>
      </FadeIn>
    </div>
  </section>;
const SolutionSection = () => {
  const pillars = [{
    icon: <Anchor className="w-8 h-8" data-id="element-28" />,
    title: 'El Ancla del Cuidador',
    description: 'Cómo regular tu propio estado de alerta en 30 segundos para no escalar la crisis de tu hijo (Si tú no estás en calma, él no podrá estarlo).'
  }, {
    icon: <Activity className="w-8 h-8" data-id="element-29" />,
    title: 'La Dieta Sensorial de Emergencia',
    description: '3 acciones físicas para bajar la carga del entorno sin tener que salir de la habitación.'
  }, {
    icon: <MessageCircle className="w-8 h-8" data-id="element-30" />,
    title: 'Comunicación No-Verbal de Rescate',
    description: 'Qué hacer (y sobre todo, qué dejar de decir) para que el cerebro de tu hijo deje de sentirse amenazado.'
  }];
  return <section className="py-24 px-4 bg-[#FAF8F5]" data-id="element-31">
      <div className="max-w-6xl mx-auto" data-id="element-32">
        <FadeIn className="text-center mb-16" data-id="element-33">
          <h2 className="font-serif text-3xl md:text-4xl text-[#2D2D2D] mb-4" data-id="element-34">
            Lo que encontrarás dentro de <br className="hidden md:block" data-id="element-35" />
            <span className="italic text-[#B5C4B1]" data-id="element-36">
              El Método: Cero Crisis
            </span>
          </h2>
          <div className="w-24 h-1 bg-[#C4704B]/30 mx-auto rounded-full" data-id="element-37"></div>
        </FadeIn>

        <div className="grid md:grid-cols-3 gap-8" data-id="element-38">
          {pillars.map((pillar, index) => <FadeIn key={index} delay={index * 0.2} data-id="element-39">
              <div className="bg-white p-8 rounded-3xl shadow-sm border border-[#D4DDD1]/30 h-full transition-all duration-300 hover:shadow-md hover:-translate-y-1" data-id="element-40">
                <div className="w-16 h-16 rounded-2xl bg-[#F5F0EB] flex items-center justify-center text-[#C4704B] mb-6" data-id="element-41">
                  {pillar.icon}
                </div>
                <h3 className="font-serif text-xl font-semibold text-[#2D2D2D] mb-4" data-id="element-42">
                  {pillar.title}
                </h3>
                <p className="font-sans text-[#2D2D2D]/70 leading-relaxed" data-id="element-43">
                  {pillar.description}
                </p>
              </div>
            </FadeIn>)}
        </div>
      </div>
    </section>;
};
const OfferSection = () => <section id="offer" className="py-24 px-4 bg-[#D4DDD1]/20" data-id="element-44">
    <div className="max-w-5xl mx-auto" data-id="element-45">
      <div className="bg-white rounded-[2.5rem] p-8 md:p-12 shadow-xl shadow-[#D4DDD1]/20 border border-white flex flex-col md:flex-row items-center gap-12" data-id="element-46">
        <FadeIn className="w-full md:w-1/2 flex justify-center" data-id="element-47">
          <div className="relative" data-id="element-48">
            <div className="absolute inset-0 bg-[#B5C4B1] rounded-3xl blur-2xl opacity-20 transform translate-y-4" data-id="element-49"></div>
            <img src="https://cdn.magicpatterns.com/uploads/6LuB3mgXu99otTXdkERaZX/portada.png" alt="Mockup del ebook El Método: Cero Crisis en una tablet" className="relative z-10 w-full max-w-md rounded-xl object-cover" data-id="element-50" />
          </div>
        </FadeIn>

        <FadeIn delay={0.2} className="w-full md:w-1/2 text-center md:text-left" data-id="element-51">
          <h2 className="font-serif text-3xl md:text-4xl text-[#2D2D2D] mb-4 leading-tight" data-id="element-52">
            Obtén hoy tu copia de <br data-id="element-53" />
            <span className="italic font-semibold" data-id="element-54">El Método: Cero Crisis</span>
          </h2>
          <p className="font-sans text-lg text-[#2D2D2D]/70 mb-8" data-id="element-55">
            Una solución práctica, neuro-afirmante y directa para recuperar la
            armonía en tu hogar.
          </p>

          <div className="mb-8 p-6 bg-[#FAF8F5] rounded-2xl border border-[#D4DDD1]/50 inline-block w-full" data-id="element-56">
            <div className="text-sm text-[#2D2D2D]/60 uppercase tracking-wider font-semibold mb-1" data-id="element-57">
              Precio hoy
            </div>
            <div className="text-4xl font-serif font-bold text-[#2D2D2D] mb-2" data-id="element-58">
              $27 USD
            </div>
            <div className="text-sm text-[#2D2D2D]/70 flex items-center justify-center md:justify-start gap-2" data-id="element-59">
              <ShieldCheck className="w-4 h-4 text-[#B5C4B1]" data-id="element-60" />
              Pago único. Acceso inmediato en tu correo.
            </div>
          </div>

          <CTAButton className="w-full md:w-auto text-base md:text-lg py-5 px-10" onClick={trackAddToCart} data-id="element-61">
            SÍ, QUIERO RECUPERAR LA CALMA EN CASA
          </CTAButton>
        </FadeIn>
      </div>
    </div>
  </section>;
const GuaranteeSection = () => <section className="py-24 px-4 bg-[#FAF8F5]" data-id="element-62">
    <div className="max-w-3xl mx-auto text-center" data-id="element-63">
      <FadeIn data-id="element-64">
        <div className="w-20 h-20 mx-auto bg-[#F5F0EB] rounded-full flex items-center justify-center mb-8" data-id="element-65">
          <ShieldCheck className="w-10 h-10 text-[#B5C4B1]" data-id="element-66" />
        </div>
        <h2 className="font-serif text-3xl text-[#2D2D2D] mb-6" data-id="element-67">
          Nuestra Promesa de Paz
        </h2>
        <p className="font-sans text-lg text-[#2D2D2D]/80 leading-relaxed max-w-2xl mx-auto" data-id="element-68">
          Lee el manual, aplica el protocolo esta noche. Si no sientes que
          tienes más claridad y herramientas para manejar la próxima crisis,
          envíanos un correo en los próximos 30 días y te reembolsaremos el 100%
          de tu dinero.{' '}
          <strong className="font-medium text-[#2D2D2D]" data-id="element-69">
            Queremos ayudarte a ti, no darte una preocupación más.
          </strong>
        </p>
      </FadeIn>
    </div>
  </section>;
const Footer = () => <footer className="bg-[#2D2D2D] text-[#FAF8F5]/60 py-12 px-4 text-center font-sans text-sm" data-id="element-70">
    <div className="max-w-4xl mx-auto" data-id="element-71">
      <p className="mb-4 font-serif text-lg text-[#FAF8F5]" data-id="element-72">
        The Meltdown Vault
      </p>
      <p data-id="element-73">
        © {new Date().getFullYear()} The Meltdown Vault. Todos los derechos
        reservados.
      </p>
      <p className="mt-2 text-xs opacity-50" data-id="element-74">
        Este sitio no es parte del sitio web de Facebook o Facebook Inc. Además,
        este sitio NO está respaldado por Facebook de ninguna manera.
      </p>
    </div>
  </footer>;
// --- Main Page Component ---
export function LandingPage() {
  useEffect(() => {
    initMetaPixel();
  }, []);
  return <div className="min-h-screen bg-[#FAF8F5] font-sans selection:bg-[#D4DDD1] selection:text-[#2D2D2D]" data-id="element-75">
      <UrgencyBar data-id="element-76" />
      <HeroSection data-id="element-77" />
      <ValidationSection data-id="element-78" />
      <SolutionSection data-id="element-79" />
      <OfferSection data-id="element-80" />
      <GuaranteeSection data-id="element-81" />
      <Footer data-id="element-82" />
    </div>;
}